import { useMemo } from "react";
import { Pressable, Text, View } from "react-native";
import { useTranslation } from "react-i18next";

import { Chip } from "../primitives/Chip";
import { ToggleRow } from "../primitives/ToggleRow";
import { useArmedConfirm } from "../primitives/useArmedConfirm";
import type { UiTheme } from "../theme";
import { makePlacementMatrixStyles } from "./placementMatrixStyles";

// PE-22 F — rows = weeks, columns = days. Sits where the logger puts its
// timeline, and is ALWAYS on screen (Antenore 2026-08-26), because it carries
// two jobs that used to live in two places:
//
//  · with NO exercise open it is the program's day/week cursor — the job the
//    spine's D-chip strip used to do, and the reason that strip could not just
//    be deleted (selectedDay drives which exercises are listed, and which day
//    a fresh one lands on).
//  · with an exercise open its cells also say whether THAT exercise is placed
//    on (week, day), and a hold writes the answer straight into the draft.
//
// ⛔ The hold is never the only way in. VoiceOver's double-tap does not reach
// onLongPress on iOS at all, and a sighted athlete gets no cue that holding
// does something a tap doesn't — both paid for once already in
// Logger/EffortBandCue.tsx:106-114. So the selected cell always exposes the
// same toggle as a plain ≥44pt switch (ToggleRow), and the hold stays a
// shortcut for people who know it.
export type PlacementMatrixProps = {
  theme: UiTheme;
  weekCount: number;
  dayCount: number;
  /** Day labels, for the spoken cell label — the grid itself shows D1..Dn. */
  dayNames: string[];
  selectedDay: number;
  selectedWeek: number;
  onSelectCell: (dayIdx: number, week: number) => void;
  /** null = navigation only: no exercise is open, so no cell has an on/off
   *  meaning and neither the hold nor the switch is offered. */
  placement: {
    /** The open exercise's name, for the switch label and the spoken cells. */
    label: string;
    isPlaced: (dayIdx: number, week: number) => boolean;
    onToggle: (dayIdx: number, week: number) => void;
  } | null;
  /** The two ＋, absorbed from the spine's day strip and week Stepper. Both
   *  axes now add here and remove here (the row −), so the Stepper could go
   *  without leaving a hole — the reason it survived the first pass. */
  onAddDay?: () => void;
  onAddWeek?: () => void;
  /** The − on a week row. Absent (or at one week) hides it. Destructive: it
   *  drops that week's column and the sets that lived only there, so it arms
   *  before it fires — the guard the spine's Stepper used to carry, moved to
   *  the row it acts on (Antenore 2026-08-26). */
  onRemoveWeek?: (week: number) => void;
};

// One row's −, and it owns its OWN two-tap state. Hoisting a single `armed`
// flag to the matrix would have forced a second "…armed for WHICH row?" field
// beside it — the pre-consolidation bookkeeping ConfirmRemoveButton was
// extracted to delete. Rendered once per row, each instance simply is its own.
// ⛔ The armed state is never colour-alone: the glyph goes − → ✕.
function WeekRemoveCell({
  week,
  label,
  confirmLabel,
  confirmWord,
  onRemove,
  styles,
}: {
  week: number;
  /** Spoken at rest. */
  label: string;
  /** Spoken while armed — the WHOLE sentence, including what is lost. */
  confirmLabel: string;
  /** Shown while armed. Short on purpose: it appears inside the row. */
  confirmWord: string;
  onRemove: (week: number) => void;
  styles: ReturnType<typeof makePlacementMatrixStyles>;
}) {
  const [armed, setArmed] = useArmedConfirm();
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={armed ? confirmLabel : label}
      onPress={() => {
        if (!armed) {
          setArmed(true);
          return;
        }
        setArmed(false);
        onRemove(week);
      }}
      hitSlop={8}
      style={[styles.rowRemove, armed && styles.rowRemoveArmed]}
    >
      <Text style={[styles.rowRemoveGlyph, armed && styles.rowRemoveGlyphArmed]}>
        {armed ? `✕ ${confirmWord}` : "−"}
      </Text>
    </Pressable>
  );
}

export function PlacementMatrix({
  theme,
  weekCount,
  dayCount,
  dayNames,
  selectedDay,
  selectedWeek,
  onSelectCell,
  placement,
  onAddDay,
  onAddWeek,
  onRemoveWeek,
}: PlacementMatrixProps) {
  const { t } = useTranslation();
  const styles = useMemo(() => makePlacementMatrixStyles(theme), [theme]);
  const canRemoveWeek = !!onRemoveWeek && weekCount > 1;

  const dayLabel = (d: number) => dayNames[d] || `D${d + 1}`;

  // Composed, not interpolated: i18n is not initialised in tests, so t()
  // returns the raw defaultValue — an interpolated one would speak
  // "{{week}}" out loud there and drift from en.json under the
  // i18nDefaultValueDrift gate.
  const cellA11yLabel = (d: number, w: number): string => {
    const where = `${t("composer.weekShort", { defaultValue: "Week" })} ${w + 1}, ${dayLabel(d)}`;
    if (!placement) return where;
    return placement.isPlaced(d, w)
      ? `${where} — ${placement.label} — ${t("composer.placed", { defaultValue: "on" })}`
      : `${where} — ${placement.label} — ${t("composer.notPlaced", { defaultValue: "off" })}`;
  };

  return (
    <View style={styles.wrap}>
      <View style={styles.headRow}>
        <View style={styles.gutter} />
        {Array.from({ length: dayCount }, (_, d) => (
          <Text
            key={d}
            style={[styles.dayHead, d === selectedDay && styles.dayHeadSelected]}
          >
            D{d + 1}
          </Text>
        ))}
        {/* Mirrors the week rows' trailing − column so the days stay aligned. */}
        {canRemoveWeek ? <View style={styles.rowRemove} /> : null}
      </View>

      {Array.from({ length: weekCount }, (_, w) => (
        <View key={w} style={styles.row}>
          <Text style={[styles.weekNum, w === selectedWeek && styles.weekNumSelected]}>
            W{w + 1}
          </Text>
          <View style={styles.cells}>
            {Array.from({ length: dayCount }, (__, d) => {
              const on = placement ? placement.isPlaced(d, w) : false;
              const here = d === selectedDay && w === selectedWeek;
              return (
                <Pressable
                  key={d}
                  accessibilityRole="button"
                  accessibilityState={{ selected: here }}
                  accessibilityLabel={cellA11yLabel(d, w)}
                  onPress={() => onSelectCell(d, w)}
                  onLongPress={
                    placement ? () => placement.onToggle(d, w) : undefined
                  }
                  style={({ pressed }) => [
                    styles.cell,
                    on && styles.cellPlaced,
                    here && styles.cellSelected,
                    pressed && styles.cellPressed,
                  ]}
                >
                  {placement ? (
                    on ? (
                      <Text style={styles.cellGlyph}>✓</Text>
                    ) : null
                  ) : (
                    <Text style={styles.cellDot}>·</Text>
                  )}
                </Pressable>
              );
            })}
          </View>
          {canRemoveWeek ? (
            <WeekRemoveCell
              week={w}
              label={`${t("composer.removeWeek", { defaultValue: "Remove week" })} — W${w + 1}`}
              confirmLabel={t("composer.removeWeekConfirm", {
                defaultValue: "Confirm — drop this week and its sets",
              })}
              confirmWord={t("program.deleteConfirm", { defaultValue: "Confirm?" })}
              onRemove={onRemoveWeek!}
              styles={styles}
            />
          ) : null}
        </View>
      ))}

      {onAddDay || onAddWeek ? (
        <View style={styles.addRow}>
          <View style={styles.addSpacer} />
          {onAddWeek ? (
            <Chip
              theme={theme}
              shape="box"
              selected={false}
              onPress={onAddWeek}
              label={`＋ ${t("composer.addWeek", { defaultValue: "Add week" })}`}
              accessibilityLabel={t("composer.addWeek", { defaultValue: "Add week" })}
            />
          ) : null}
          {onAddDay ? (
            <Chip
              theme={theme}
              shape="box"
              selected={false}
              onPress={onAddDay}
              label={`＋ ${t("composer.addDay", { defaultValue: "Add day" })}`}
              accessibilityLabel={t("composer.addDay", { defaultValue: "Add day" })}
            />
          ) : null}
        </View>
      ) : null}

      {placement ? (
        <ToggleRow
          theme={theme}
          label={`W${selectedWeek + 1} · ${dayLabel(selectedDay)}`}
          sublabel={t("composer.placementHint", {
            defaultValue: "Hold a cell to place or remove it here.",
          })}
          value={placement.isPlaced(selectedDay, selectedWeek)}
          onValueChange={() => placement.onToggle(selectedDay, selectedWeek)}
          accessibilityLabel={cellA11yLabel(selectedDay, selectedWeek)}
        />
      ) : null}
    </View>
  );
}
